import React, {
  useEffect, useMemo, useState,
} from 'react';
import {
  Modal,
  Input,
  Checkbox,
  Select,
  Typography,
  Spin,
  message,
  Divider,
  Tag,
  Row,
  Col,
} from 'antd';
import PropTypes from 'prop-types';
import * as api from 'util/api';

const { Text } = Typography;

const RoleModal = ({
  visible,
  onClose,
  existingRole,
  routes,
  permissionsList,
  users,
}) => {
  const isEditMode = !!existingRole;

  const [roleName, setRoleName] = useState('');
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);
  const [assignedUserUids, setAssignedUserUids] = useState([]);
  const [loadingUserRoles, setLoadingUserRoles] = useState(false);
  const [saving, setSaving] = useState(false);

  const permissionKeyToId = useMemo(
    () => new Map((permissionsList || []).map((p) => [p.key, p.id])),
    [permissionsList],
  );

  const routesWithPermissionId = useMemo(
    () => (routes || [])
      .filter((r) => permissionKeyToId.has(r.permission))
      .map((r) => ({
        ...r,
        permissionId: permissionKeyToId.get(r.permission),
      })),
    [routes, permissionKeyToId],
  );

  const additionalPermissions = useMemo(() => {
    const routePermissionKeys = new Set(routesWithPermissionId.map((r) => r.permission));
    return (permissionsList || []).filter((p) => !routePermissionKeys.has(p.key));
  }, [permissionsList, routesWithPermissionId]);

  useEffect(() => {
    if (!visible) return;

    if (isEditMode && existingRole) {
      setRoleName(existingRole.name || '');
      setSelectedPermissionIds(
        (existingRole.permissions || []).map((p) => p.id),
      );
    } else {
      setRoleName('');
      setSelectedPermissionIds([]);
      setAssignedUserUids([]);
    }
  }, [visible, existingRole]);

  useEffect(() => {
    if (!visible || !isEditMode || !existingRole) {
      setAssignedUserUids([]);
      return;
    }
    setLoadingUserRoles(true);
    api.getUserRoles()
      .then((allUserRoles) => {
        const uids = (allUserRoles || [])
          .filter((ur) => ur.roleId === existingRole.id)
          .map((ur) => ur.firebaseUid);
        setAssignedUserUids([...new Set(uids)]);
      })
      .catch(() => message.error('Failed to load user assignments'))
      .finally(() => setLoadingUserRoles(false));
  }, [visible, existingRole?.id]);

  const handlePermissionToggle = (permissionId, checked) => {
    setSelectedPermissionIds((prev) => (checked
      ? [...prev, permissionId]
      : prev.filter((id) => id !== permissionId)));
  };

  const handleUserSelectionChange = (uids) => {
    setAssignedUserUids(uids);
  };

  const handleSave = async () => {
    const name = roleName.trim();
    if (!name) {
      message.warning('Please enter a role name');
      return;
    }
    setSaving(true);

    try {
      let roleId;
      if (isEditMode) {
        await api.putRole(existingRole.id, {
          permissionIds: selectedPermissionIds,
        });
        roleId = existingRole.id;
      } else {
        const created = await api.postRole({
          name,
          permissionIds: selectedPermissionIds,
        });
        roleId = created.id;
      }

      const currentUsers = users || [];

      // Determine which users to add/remove
      let previouslyAssignedUids = [];
      if (isEditMode) {
        const allUserRoles = await api.getUserRoles();
        previouslyAssignedUids = (allUserRoles || [])
          .filter((ur) => ur.roleId === roleId && ur.firebaseUid)
          .map((ur) => ur.firebaseUid);
      }

      const toAdd = assignedUserUids
        .filter((uid) => !previouslyAssignedUids.includes(uid));
      const toRemove = previouslyAssignedUids
        .filter((uid) => !assignedUserUids.includes(uid));

      const addPromises = toAdd.map(async (uid) => {
        const user = currentUsers.find((u) => u.uid === uid);
        const existing = await api.getUserRoles(uid);
        const ids = (existing || []).map((ur) => ur.roleId).filter(Boolean);
        const merged = [...new Set([...ids, roleId])];
        return api.putUserRoles({
          firebaseUid: uid,
          email: user?.email || null,
          roleIds: merged,
        });
      });

      const removePromises = toRemove.map(async (uid) => {
        const user = currentUsers.find((u) => u.uid === uid);
        const existing = await api.getUserRoles(uid);
        const remaining = (existing || [])
          .map((ur) => ur.roleId)
          .filter((rid) => rid !== roleId);
        return api.putUserRoles({
          firebaseUid: uid,
          email: user?.email || null,
          roleIds: remaining,
        });
      });

      await Promise.all([...addPromises, ...removePromises]);

      message.success(isEditMode
        ? `Updated "${roleName}"`
        : `Created "${roleName}"`);
      onClose(true);
    } catch (err) {
      message.error(
        isEditMode ? 'Failed to update role' : 'Failed to create role',
      );
    } finally {
      setSaving(false);
    }
  };

  const userOptions = useMemo(() => {
    if (!users) return [];
    const assigned = new Set(assignedUserUids);
    const sortedUsers = [...users].sort((a, b) => {
      const aAssigned = assigned.has(a.uid);
      const bAssigned = assigned.has(b.uid);
      if (aAssigned && !bAssigned) return -1;
      if (!aAssigned && bAssigned) return 1;
      return (a.name || '').localeCompare(b.name || '');
    });
    return sortedUsers.map((u) => ({
      label: `${u.name || u.email} ${u.email ? `(${u.email})` : ''}`,
      value: u.uid,
    }));
  }, [users, assignedUserUids]);

  return (
    <Modal
      title={isEditMode ? `Edit role: ${existingRole?.name}` : 'Create new role'}
      visible={visible}
      onCancel={() => onClose(false)}
      onOk={handleSave}
      okText={isEditMode ? 'Save' : 'Create'}
      confirmLoading={saving}
      okButtonProps={{ disabled: !roleName.trim() }}
      width={640}
      maskClosable={false}
    >
      <div style={{ marginBottom: 16 }}>
        <Text strong>Role name</Text>
        <Input
          placeholder="e.g. Sales Team, Brewery Staff"
          value={roleName}
          onChange={(e) => setRoleName(e.target.value)}
          style={{ marginTop: 4 }}
          disabled={saving}
        />
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <div style={{ marginBottom: 16 }}>
        <Text strong>
          Permissions
          {permissionsList.length > 0 && (
            <Tag style={{ marginLeft: 8 }}>
              {selectedPermissionIds.length}
              /
              {permissionsList.length}
              {' '}
              selected
            </Tag>
          )}
        </Text>
        <Row gutter={[8, 4]} style={{ marginTop: 8 }}>
          {routesWithPermissionId.map((route) => (
            <Col span={12} key={route.permission}>
              <Checkbox
                checked={selectedPermissionIds.includes(route.permissionId)}
                onChange={(e) => handlePermissionToggle(route.permissionId, e.target.checked)}
                disabled={saving}
              >
                {route.menuLabel}
              </Checkbox>
            </Col>
          ))}
        </Row>
        {additionalPermissions.length > 0 && (
          <>
            <Text type="secondary" style={{ display: 'block', marginTop: 12 }}>
              Additional actions
            </Text>
            <Row gutter={[8, 4]} style={{ marginTop: 4 }}>
              {additionalPermissions.map((permission) => (
                <Col span={12} key={permission.key}>
                  <Checkbox
                    checked={selectedPermissionIds.includes(permission.id)}
                    onChange={(e) => handlePermissionToggle(permission.id, e.target.checked)}
                    disabled={saving}
                  >
                    {permission.key}
                  </Checkbox>
                </Col>
              ))}
            </Row>
          </>
        )}
      </div>

      <Divider style={{ margin: '12px 0' }} />

      <div>
        <Text strong>Assign users</Text>
        {loadingUserRoles ? (
          <div style={{ textAlign: 'center', padding: 12 }}>
            <Spin size="small" />
          </div>
        ) : (
          <Select
            mode="multiple"
            placeholder="Search and select users…"
            value={assignedUserUids}
            onChange={handleUserSelectionChange}
            options={userOptions}
            filterOption={(input, option) => (
              (option?.label || '').toLowerCase().includes(input.toLowerCase())
            )}
            style={{ width: '100%', marginTop: 4 }}
            disabled={saving}
            maxTagCount="responsive"
            showSearch
          />
        )}
      </div>
    </Modal>
  );
};

RoleModal.propTypes = {
  visible: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  existingRole: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    permissions: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      key: PropTypes.string,
    })),
  }),
  routes: PropTypes.arrayOf(PropTypes.shape({
    menuLabel: PropTypes.string,
    permission: PropTypes.string,
  })),
  permissionsList: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    key: PropTypes.string,
  })),
  users: PropTypes.arrayOf(PropTypes.shape({
    uid: PropTypes.string,
    name: PropTypes.string,
    email: PropTypes.string,
  })),
};

RoleModal.defaultProps = {
  existingRole: null,
  routes: [],
  permissionsList: [],
  users: [],
};

export default RoleModal;
